/**
 * LotAllocationFormDialog — bám Access spec `frmFactoryQryLotAllocationAdd`
 * (`plan/access_all/form_frmFactoryQryLotAllocationAdd.txt`). Cùng pattern với
 * IssueFormDialog: chọn nguồn → readonly Pending → nhập allocated ≤ Pending.
 *
 * Layout (Access Left/Top coords; left col Left=2640, right col Left=8532):
 *   Row 1 (Top=240):   lot_id (combo)       | stack_id (combo)
 *   Row 2 (Top=624):   lot_id_bag (RO)      | grn_id (combo)
 *   Row 3 (Top=1008):  lot_id_kg (RO)       | grn_id_bag (RO)
 *   Row 4 (Top=1392):                       | grn_id_kg (RO)
 *   Row 5 (Top=1764):  "Allocation" label   |
 *   Row 6 (Top=2148):  bag_allocated        | kg_allocated
 *
 * Required (line 2386): `strCheck = ",stack_id,lot_id,bag_allocated,kg_allocated,"`
 *   (grn_id KHÔNG required — có thể allocate theo stack balance khi chưa chọn GRN.)
 * Validate (BeforeUpdate):
 *   bag_allocated ≤ lot_id_bag AND ≤ grn_id_bag  → "Bag Allocated exceeds Pending Bags"
 *   kg_allocated  ≤ lot_id_kg  AND ≤ grn_id_kg   → "Kg Allocated exceeds Pending Kg"
 * Cascades:
 *   lot_id  → recompute lot_id_bag / lot_id_kg.
 *   stack_id → reset grn_id RowSource (receive by stack), clear grn; recompute.
 *   grn_id  → recompute grn_id_bag / grn_id_kg; new-mode auto-fill allocation = Pending.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  useGetLotAllocationDoOptionsQuery,
  useGetLotAllocationOptionsQuery,
  useLazyGetLotAllocationGrnOptionsQuery,
  useLazyGetLotAllocationSnapshotQuery,
  type FactoryBRowPayload,
  type FactoryCell,
  type LotAllocationDoOption,
} from '@/store/api/factoryApi'
import { FORM_GRID_2COL, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

type LotAllocationRow = { id: string | number } & Record<string, FactoryCell>

// Search combo DO No theo cả dòng phụ (sale_contract / loading_date / from_warehouse)
// — getOptionLabel chỉ trả do_no nên MUI default filter sẽ KHÔNG match các field
// phụ đang hiện trong dropdown (skill /convert-form anti-pattern #55).
const doComboFilter = createFilterOptions<LotAllocationDoOption>({
  stringify: (o) =>
    [o.do_no, o.sale_contract, o.loading_date, o.from_warehouse].filter(Boolean).join(' '),
  trim: true,
})

interface DraftState {
  lot_id: string
  stack_id: string
  grn_id: string
  do_no: string
  bag_allocated: string
  kg_allocated: string
}

const EMPTY_DRAFT: DraftState = {
  lot_id: '',
  stack_id: '',
  grn_id: '',
  do_no: '',
  bag_allocated: '',
  kg_allocated: '',
}

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Bg cho field readonly (Pending Bag/Kg — unbound, set bởi snapshot).
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: (theme: { palette: { text: { secondary: string } } }) =>
      theme.palette.text.secondary,
    fontWeight: 600,
  },
}

// Access `Format = "#,#00;(#,#00);;0"` + DecimalPlaces=2 → `#,##0.00`.
const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatNumber = (n: number | null): string =>
  n == null
    ? '—'
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

interface LotAllocationFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  row: LotAllocationRow | null
  saving: boolean
  factoryCode: string
  /** Permission gating — false → ẩn Save + show read-only banner (layer 4). */
  canEdit?: boolean
  onClose: () => void
  onSave: (payload: FactoryBRowPayload) => void
}

export function LotAllocationFormDialog({
  open,
  mode,
  row,
  saving,
  factoryCode,
  canEdit = true,
  onClose,
  onSave,
}: LotAllocationFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [lotBag, setLotBag] = useState<number | null>(null)
  const [lotKg, setLotKg] = useState<number | null>(null)
  const [grnBag, setGrnBag] = useState<number | null>(null)
  const [grnKg, setGrnKg] = useState<number | null>(null)
  const [snapshotError, setSnapshotError] = useState('')

  // New-mode auto-fill (Access check_number): allocation = Pending của phía GRN
  // khi stack/grn đổi. Track key để KHÔNG auto-fill lại khi chỉ lot đổi.
  const prevGrnKeyRef = useRef<string | null>(null)

  const optionsQuery = useGetLotAllocationOptionsQuery(
    { factoryCode },
    { skip: !open },
  )

  // DO No RowSource (delivery_order_scs). Edit: truyền do_no đang lưu để BE
  // inject nếu DO đã rời list (memory: autocomplete inject current value).
  const doOptionsQuery = useGetLotAllocationDoOptionsQuery(
    { factoryCode, doNo: mode === 'edit' ? String(row?.do_no ?? '') : undefined },
    { skip: !open },
  )
  const doOptions = doOptionsQuery.data ?? []
  const selectedDo = doOptions.find((o) => o.do_no === draft.do_no) ?? null

  const [triggerGrnOptions, grnOptionsState] = useLazyGetLotAllocationGrnOptionsQuery()

  // Combo strict (Access `LimitToList = True`): inject value đang lưu nếu nằm
  // ngoài RowSource (Edit mode, lot/stack/grn đã rời list) để Autocomplete
  // không hiện blank + tắt warning "value invalid" (memory: inject current value).
  const injectCurrent = (base: string[], cur: string): string[] => {
    const c = cur.trim()
    return c && !base.includes(c) ? [c, ...base] : base
  }
  const lotIdOptions = useMemo(
    () => injectCurrent(optionsQuery.data?.lot_ids ?? [], draft.lot_id),
    [optionsQuery.data, draft.lot_id],
  )
  const stackIdOptions = useMemo(
    () => injectCurrent(optionsQuery.data?.stack_ids ?? [], draft.stack_id),
    [optionsQuery.data, draft.stack_id],
  )
  const grnIdOptions = useMemo(
    () => injectCurrent(grnOptionsState.data ?? [], draft.grn_id),
    [grnOptionsState.data, draft.grn_id],
  )

  const [triggerSnapshot, { isFetching: snapshotLoading }] =
    useLazyGetLotAllocationSnapshotQuery()

  const allocationId = useMemo(() => {
    if (mode !== 'edit' || !row) return null
    const v = row.id
    const n = typeof v === 'number' ? v : parseInt(String(v), 10)
    return Number.isFinite(n) ? n : null
  }, [mode, row])

  // Hydrate draft khi dialog mở. Deps `[open, mode, row]` (memory: hydrate deps).
  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      setDraft({
        lot_id: String(row.lot_id ?? ''),
        stack_id: String(row.stack_id ?? ''),
        grn_id: String(row.grn_id ?? ''),
        do_no: String(row.do_no ?? ''),
        bag_allocated: formatStandard(row.bag_allocated as number | string | null),
        kg_allocated: formatStandard(row.kg_allocated as number | string | null),
      })
    } else {
      setDraft(EMPTY_DRAFT)
    }
    setLotBag(null)
    setLotKg(null)
    setGrnBag(null)
    setGrnKg(null)
    setSnapshotError('')
    // Edit mode: seed key bằng row hiện tại để KHÔNG auto-overwrite allocated.
    prevGrnKeyRef.current =
      mode === 'edit' && row
        ? `${String(row.stack_id ?? '')}|${String(row.grn_id ?? '')}`
        : null
  }, [open, mode, row])

  // grn_id RowSource keyed by stack (Access stack_id_AfterUpdate). Refetch khi
  // stack đổi; stack rỗng → list rỗng.
  useEffect(() => {
    if (!open) return
    const sid = draft.stack_id.trim()
    if (!sid) return
    void triggerGrnOptions({ factoryCode, stackId: sid })
  }, [open, draft.stack_id, factoryCode, triggerGrnOptions])

  // Chọn Stack mới → GRN options load lại → auto chọn GRN ĐẦU TIÊN nếu có.
  // Chỉ set khi grn_id đang trống (stack change đã clear grn) → KHÔNG ghi đè
  // value Edit hydrate hoặc GRN user tự chọn.
  useEffect(() => {
    if (!open) return
    const list = grnOptionsState.data
    if (!list || list.length === 0) return
    setDraft((prev) => (prev.grn_id.trim() ? prev : { ...prev, grn_id: list[0] }))
  }, [open, grnOptionsState.data])

  // Snapshot Pending Bag/Kg (Access lot_id_AfterUpdate + check_number). Fetch
  // khi lot_id / stack_id / grn_id đổi.
  useEffect(() => {
    if (!open) return
    const lid = draft.lot_id.trim()
    const sid = draft.stack_id.trim()
    const gid = draft.grn_id.trim()
    if (!lid && !sid && !gid) {
      setLotBag(null)
      setLotKg(null)
      setGrnBag(null)
      setGrnKg(null)
      setSnapshotError('')
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const res = await triggerSnapshot({
          factoryCode,
          lotId: lid || null,
          stackId: sid || null,
          grnId: gid || null,
          allocationId: mode === 'edit' ? allocationId : null,
        }).unwrap()
        if (cancelled) return
        setLotBag(res.lot_id_bag)
        setLotKg(res.lot_id_kg)
        setGrnBag(res.grn_id_bag)
        setGrnKg(res.grn_id_kg)
        setSnapshotError('')
        // New-mode auto-fill: chỉ khi phía GRN (stack|grn) đổi — mirror
        // check_number set bag/kg_allocated = Pending. Lot đổi → không đụng.
        const grnKey = `${sid}|${gid}`
        if (mode === 'new' && grnKey !== prevGrnKeyRef.current) {
          setDraft((prev) => ({
            ...prev,
            bag_allocated: formatStandard(res.grn_id_bag),
            kg_allocated: formatStandard(res.grn_id_kg),
          }))
        }
        prevGrnKeyRef.current = grnKey
      } catch {
        if (cancelled) return
        setSnapshotError('Could not load pending balances for the current selection')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, draft.lot_id, draft.stack_id, draft.grn_id, factoryCode, allocationId, mode, triggerSnapshot])

  const lotIdEmpty = draft.lot_id.trim().length === 0
  const stackIdEmpty = draft.stack_id.trim().length === 0
  const bagNum = parseNumeric(draft.bag_allocated)
  const kgNum = parseNumeric(draft.kg_allocated)
  const bagEmpty = bagNum == null
  const kgEmpty = kgNum == null

  // BeforeUpdate: allocated ≤ Pending của CẢ hai phía (lot + grn).
  const bagOver =
    bagNum != null &&
    ((lotBag != null && bagNum > lotBag) || (grnBag != null && bagNum > grnBag))
  const kgOver =
    kgNum != null &&
    ((lotKg != null && kgNum > lotKg) || (grnKg != null && kgNum > grnKg))

  const canSave =
    canEdit &&
    !lotIdEmpty &&
    !stackIdEmpty &&
    !bagEmpty &&
    !kgEmpty &&
    !bagOver &&
    !kgOver &&
    !saving

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  // stack_id đổi → clear grn_id + grn Pending (Access stack_id_AfterUpdate).
  const handleStackChange = (value: string) =>
    setDraft((prev) =>
      prev.stack_id === value ? prev : { ...prev, stack_id: value, grn_id: '' },
    )

  const handleNumericBlur = <K extends keyof DraftState>(key: K) => () => {
    const raw = draft[key]
    const formatted = formatStandard(raw)
    if (formatted !== raw) setDraft((prev) => ({ ...prev, [key]: formatted }))
  }

  // btnSave_Click (line 2308): isValid(Me,strCheck) → Update_Insert. PK `id`
  // serial nên KHÔNG gửi; parent merge id từ pendingAction.row khi edit.
  const handleSave = () => {
    if (!canSave) return
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    const payload: FactoryBRowPayload = {
      lot_id: draft.lot_id.trim(),
      stack_id: draft.stack_id.trim(),
      grn_id: str(draft.grn_id),
      do_no: str(draft.do_no),
      bag_allocated: parseNumeric(draft.bag_allocated),
      kg_allocated: parseNumeric(draft.kg_allocated),
    }
    onSave(payload)
  }

  // btnCancel_Click (line 2299): Me.Undo + Close. Block khi saving.
  const handleClose = () => {
    if (saving) return
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(640px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          noWrap
          title={`${mode === 'new' ? 'New' : 'Edit'} — Factory · Lot Allocation`}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {mode === 'new' ? 'New' : 'Edit'} —
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {' Factory ·'}
          </Box>
          {' Lot Allocation'}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.5 }}
        >
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          {/* btnSave (Access btnSave, FontWeight=700) — primary action. */}
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          {/* btnCancel — block khi saving. */}
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          ...compactInputSx,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          pt: 1.5,
        }}
      >
        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 1.25, py: 0.25 }}>
            Read-only — you don't have edit permission for this factory.
          </Alert>
        ) : null}
        {snapshotError ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            {snapshotError}
          </Alert>
        ) : null}

        {/* 2-col grid, explicit gridRow/gridColumn bám Access Top (memory: form
            grid align). Left col = nguồn LOT; right col = nguồn STACK/GRN. */}
        <Box sx={FORM_GRID_2COL}>
          {/* Row 1 (Top=240) — lot_id | stack_id. Strict (Access LimitToList=True):
             KHÔNG freeSolo — chỉ chọn từ RowSource. */}
          <Autocomplete
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } }}
            size="small"
            options={lotIdOptions}
            value={draft.lot_id || null}
            onChange={(_, v) => handleChange('lot_id', v ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Lot ID" required fullWidth autoFocus={mode === 'new'} />
            )}
          />
          <Autocomplete
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 } }}
            size="small"
            options={stackIdOptions}
            value={draft.stack_id || null}
            onChange={(_, v) => handleStackChange(v ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Stack ID" required fullWidth />
            )}
          />

          {/* Row 2 (Top=624) — lot_id_bag (RO) | grn_id */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 2 }, ...readonlyFieldSx }}
            label="Pending Bag (Lot)"
            size="small"
            fullWidth
            value={formatNumber(lotBag)}
            InputProps={{
              readOnly: true,
              endAdornment: snapshotLoading ? <CircularProgress size={14} /> : null,
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={numericInputProps}
          />
          <Autocomplete
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 2 } }}
            size="small"
            disabled={stackIdEmpty}
            loading={grnOptionsState.isFetching}
            options={grnIdOptions}
            value={draft.grn_id || null}
            onChange={(_, v) => handleChange('grn_id', v ?? '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="GRN ID"
                fullWidth
                helperText={stackIdEmpty ? 'Select a Stack ID first.' : undefined}
              />
            )}
          />

          {/* Row 3 (Top=1008) — lot_id_kg (RO) | grn_id_bag (RO) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 3 }, ...readonlyFieldSx }}
            label="Pending Kg (Lot)"
            size="small"
            fullWidth
            value={formatNumber(lotKg)}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={numericInputProps}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 3 }, ...readonlyFieldSx }}
            label="Pending Bag (GRN/Stack)"
            size="small"
            fullWidth
            value={formatNumber(grnBag)}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={numericInputProps}
          />

          {/* Row 4 (Top=1392) — DO No (addition 2026-06-04, ngoài Access spec)
              | grn_id_kg (RO). DO No đặt ở ô trống Row 4 trái để không phá grid
              Access; lưu do_no, hiển thị Sale Contract / Loading Date để chọn
              đúng DO (view join lại). */}
          <Autocomplete<LotAllocationDoOption, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 4 } }}
            size="small"
            options={doOptions}
            value={selectedDo}
            loading={doOptionsQuery.isFetching}
            getOptionLabel={(o) => o.do_no}
            isOptionEqualToValue={(o, v) => o.do_no === v.do_no}
            filterOptions={doComboFilter}
            onChange={(_, v) => handleChange('do_no', v?.do_no ?? '')}
            renderOption={(props, o) => (
              <li {...props} key={o.do_no}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {o.do_no}
                  </Typography>
                  {[o.sale_contract, o.loading_date, o.from_warehouse]
                    .filter(Boolean)
                    .length > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={TEXT_TRUNCATE}>
                      {[o.sale_contract, o.loading_date, o.from_warehouse]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  ) : null}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="DO No"
                fullWidth
                helperText={
                  selectedDo
                    ? [selectedDo.sale_contract, selectedDo.loading_date]
                        .filter(Boolean)
                        .join(' · ') || undefined
                    : undefined
                }
              />
            )}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 4 }, ...readonlyFieldSx }}
            label="Pending Kg (GRN/Stack)"
            size="small"
            fullWidth
            value={formatNumber(grnKg)}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={numericInputProps}
          />

          {/* Row 5 (Top=1764) — "Allocation" section label (left only).
              Access spec là đỏ đậm (ForeColor=255, FontWeight=700) nhưng đứng
              lẻ trông như báo lỗi → cố ý đổi sang caption xám IN HOA cho gọn
              (chị Đẹp duyệt). Giữ nguyên vị trí grid. */}
          <Typography
            sx={{
              gridColumn: { xs: 1, sm: 1 },
              gridRow: { sm: 5 },
              alignSelf: 'center',
              fontWeight: 600,
              fontSize: '0.6875rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
            color="text.secondary"
          >
            Allocation
          </Typography>

          {/* Row 6 (Top=2148) — bag_allocated | kg_allocated */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 6 } }}
            label="Bag Allocated"
            size="small"
            fullWidth
            required
            type="text"
            value={draft.bag_allocated}
            onChange={(e) => handleChange('bag_allocated', e.target.value)}
            onBlur={handleNumericBlur('bag_allocated')}
            error={bagOver}
            helperText={bagOver ? 'Bag Allocated exceeds Pending Bags' : undefined}
            inputProps={numericInputProps}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 6 } }}
            label="Kg Allocated"
            size="small"
            fullWidth
            required
            type="text"
            value={draft.kg_allocated}
            onChange={(e) => handleChange('kg_allocated', e.target.value)}
            onBlur={handleNumericBlur('kg_allocated')}
            error={kgOver}
            helperText={kgOver ? 'Kg Allocated exceeds Pending Kg' : undefined}
            inputProps={numericInputProps}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default LotAllocationFormDialog
